import { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import helpers from "../helpers";
import isEqual from "lodash/isEqual";
import { useQueryClient, useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import useMutation from "./useMutation";
import useTeamID from "./useTeamID";
import getAccessToken from "./getAccessToken";

const { REACT_APP_API_URL } = process.env;

const can = (user, permission, payload) => {
  return true;
};

const request = (accessToken) => {
  const options = {};
  if (accessToken) {
    options.headers = { Authorization: `Bearer ${accessToken}` };
  }
  return fetch(`${REACT_APP_API_URL}/users`, options).then((res) => res.json());
};

const Hook = () => {
  const auth0 = useAuth0();
  const [state, setState] = useState({});
  const [team, setTeam] = useState();
  const teamID = useTeamID();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  const accessToken = getAccessToken();

  const query = useQuery(
    ["users", accessToken.value],
    () => request(accessToken.value),
    {
      enabled: !!accessToken.value,
      refetchOnWindowFocus: false,
      placeholderData: {},
    }
  );

  const [mutate, mutation] = useMutation("users");

  const actions = {};
  actions.logout = async () => {
    try {
      window.localStorage.clear();
    } catch {}
    try {
      window.sessionStorage.clear();
    } catch {}
    try {
      await auth0.logout();
    } catch {}
  };

  actions.can = (permission, payload) =>
    state.isStaff || can(state, permission, payload);
  actions.has = (role, flag) =>
    state.isStaff || (team?.permissions?.[role] ?? flag);

  actions.onChange = (payload) => {
    const user = JSON.clone({
      ...query.data,
      ...payload,
    });

    if (user.language === "es") {
      i18n.changeLanguage("es-AR");
    } else if (user.language !== i18n.language) {
      i18n.changeLanguage(user.language || "es-AR");
    }

    queryClient.setQueryData(["users", accessToken.value], user);

    return user;
  };

  actions.save = (payload) => {
    mutate(
      JSON.clone({
        ...query.data,
        ...payload,
        verified: undefined,
      })
    );
  };

  useEffect(() => {
    if (query.data.language === "es" || i18n.language === "es") {
      i18n.changeLanguage("es-AR");
    } else if (query.data.language && query.data.language !== i18n.language) {
      i18n.changeLanguage(query.data.language || "es-AR");
    }
  }, [query.data.language, i18n.language]);

  // Bug real (auditoría 2026-08-14): JSON.stringify(query.data) (potencialmente
  // el perfil completo + array de teams) corría en CADA render de CADA
  // componente que llama useUser() — un hook usado en casi toda la app.
  // Memoizado: sólo se recalcula si la referencia realmente cambió (query.data
  // ya tiene structural sharing de react-query — no cambia de referencia entre
  // renders si el contenido es igual), preservando la comparación por
  // VALOR (no sólo por referencia) que estos efectos necesitan, sin pagar el
  // costo de serializar en cada render.
  const queryDataKey = useMemo(() => JSON.stringify(query.data), [query.data]);
  const auth0UserKey = useMemo(() => JSON.stringify(auth0.user), [auth0.user]);
  const whitelistKey = useMemo(() => JSON.stringify(i18n.options.whitelist), [i18n.options.whitelist]);

  useEffect(() => {
    if (query.data?.id && auth0.user?.sub) {
      const state = {};
      state.uid = helpers.getEmail(
        auth0.user.email || auth0.user.phone || auth0.user.sub
      );
      state.name = auth0.user?.name;
      state.email = auth0.user?.email;
      state.phone = auth0.user?.phone;
      state.verified = auth0.user?.email_verified || auth0.user?.phone_verified;
      setState(JSON.clone({ ...query.data, ...state }));
    }
  }, [queryDataKey, auth0UserKey]);

  useEffect(() => {
    // Bug real (auditoría 2026-08-14): este efecto y el de arriba (datos
    // de usuario) pueden disparar en el MISMO commit (ej. Auth0 y la
    // query de /users ya resueltos desde el montaje) — leer `state` del
    // closure de render (stale, no ve el setState del otro efecto dentro
    // del mismo commit) hacía que éste PISE por completo el uid/email/
    // name/phone/verified que el efecto de arriba acababa de calcular.
    // setState con función evita el problema: siempre parte del state más
    // reciente al momento de aplicarse, sin importar qué otro efecto haya
    // corrido antes en el mismo commit.
    //
    // Bug real (auditoría 2026-08-18, LOOP INFINITO EN PRODUCCIÓN): este
    // mismo efecto tenía `stateKey` (derivado del propio `state` que
    // actualiza) en su array de dependencias. El guard de salida
    // (`whitelist.includes(prev.language)`) sólo corta el loop si
    // `i18n.language` termina coincidiendo LITERAL con uno de los strings
    // del whitelist — si no coincide (ej. un idioma legado guardado en el
    // perfil del usuario, o cualquier valor de i18n.language que no sea
    // exactamente uno de los 7 configurados), el efecto sigue devolviendo
    // un objeto NUEVO (mismos valores, otra referencia) en cada commit:
    // stateKey cambia -> el efecto dispara de nuevo -> sin fin. Los tests
    // existentes no lo agarraban porque sólo probaban idiomas ya
    // whitelisteados (el guard corta a la primera). El closure usa
    // `prev` (vía la forma funcional de setState), no el `state` del
    // render — nunca necesitó `state`/`stateKey` como dependencia.
    setState((prev) => {
      if (i18n.options.whitelist.includes(prev.language) || !i18n.language) {
        return prev;
      }
      let [language, country] = i18n.language.split("-");
      if (country === "419") {
        country = "ar";
      } else if (!country && language === "es") {
        country = "ar";
      }
      return {
        ...prev,
        country: country?.toLowerCase() || prev.country,
        language: i18n.language,
      };
    });
  }, [i18n.language, whitelistKey]);

  useEffect(() => {
    const team = query.data?.teams?.find((team) => team.id === teamID);
    setTeam({ ...team });
  }, [queryDataKey, teamID, state.uid]);

  return {
    team,
    mutation,
    query,
    state,
    actions,
  };
};

export default Hook;
